// src/services/pdf/pdfBranding.js
// Shared header, footer, section helpers for all PDF documents.
// Uses jsPDF instance passed in. All functions mutate doc in place.

import { COLORS, COMPANY, formatDateShort } from "./pdfConfig.js";

// =========================
// Logo Cache (loaded once)
// =========================
let _logoWhiteDataUrl = null;
let _logoBlackDataUrl = null;

// El logotipo mas grande que se imprime ocupa 47,4 mm (la cabecera del
// contrato); a 300 ppp eso son ~560 px. Los ficheros vienen a 4783 px, ocho
// veces y media de mas.
const LOGO_MAX_WIDTH = 700;

async function loadImage(url, { maxWidth = LOGO_MAX_WIDTH } = {}) {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            // Reducir aqui no es cosmetica: el canvas devuelve siempre RGBA y
            // jsPDF acaba metiendo el mapa de bits en el PDF, asi que a tamano
            // original un contrato pesa 34 MB en lugar de unos cientos de KB.
            const scale = Math.min(1, maxWidth / img.width);
            const canvas = document.createElement("canvas");
            canvas.width = Math.max(1, Math.round(img.width * scale));
            canvas.height = Math.max(1, Math.round(img.height * scale));

            const ctx = canvas.getContext("2d");
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = () => resolve(null); // graceful fallback
        img.src = url;
    });
}

export async function preloadLogos() {
    if (!_logoWhiteDataUrl) {
        _logoWhiteDataUrl = await loadImage(
            new URL("../../assets_toldos/logos/VECTOR_LOGOS_LOSTOLDOS_BLANCO.png", import.meta.url).href
        );
    }
    if (!_logoBlackDataUrl) {
        // OJO con los nombres de los ficheros, que enganan:
        // "..._NEGRO FONDO BLANCO.png" son letras BLANCAS sobre fondo NEGRO
        // macizo, y sobre el papel blanco de un PDF sale un rectangulo negro.
        // El logotipo en tinta negra, que es el que usa la plantilla del
        // contrato, es "..._NEGRO.png".
        _logoBlackDataUrl = await loadImage(
            new URL("../../assets_toldos/logos/VECTOR_LOGOS_LOSTOLDOS_NEGRO.png", import.meta.url).href
        );
    }
}

// =========================
// Legacy Header (for other docs)
// =========================
export function drawHeader(doc, { title = "", subtitle = "", docNumber = "" } = {}) {
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFillColor(...COLORS.PRIMARY);
    doc.rect(0, 0, pageWidth, 44, "F");
    doc.setFillColor(...COLORS.DARK);
    doc.rect(0, 44, pageWidth, 2, "F");
    if (_logoWhiteDataUrl) {
        try { doc.addImage(_logoWhiteDataUrl, "PNG", 12, 6, 32, 32); } catch { }
    }
    doc.setTextColor(...COLORS.WHITE);
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    const nameX = _logoWhiteDataUrl ? 48 : 15;
    doc.text("LOS TOLDOS", nameX, 20);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("ENTERTAINMENT", nameX, 27);
    if (title) {
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text(title, pageWidth - 15, 18, { align: "right" });
    }
    if (docNumber) {
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text(`Nº ${docNumber}`, pageWidth - 15, 26, { align: "right" });
    }
    if (subtitle) {
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.text(subtitle, pageWidth - 15, 33, { align: "right" });
    }
    return 54;
}

// =========================
// Legacy Section Title
// =========================
export function drawSectionTitle(doc, y, title, { number = "" } = {}) {
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setTextColor(...COLORS.DARK);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    const label = number ? `${number}. ${title}` : title;
    doc.text(label, 15, y);
    doc.setDrawColor(...COLORS.PRIMARY);
    doc.setLineWidth(0.8);
    doc.line(15, y + 2, pageWidth - 15, y + 2);
    return y + 10;
}

// =========================
// Legacy Info Table
// =========================
export function drawInfoTable(doc, y, items, { labelWidth = 50, startX = 15 } = {}) {
    doc.setFontSize(10);
    items.forEach((item) => {
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...COLORS.TEXT);
        doc.text(item.label, startX, y);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...COLORS.BLACK);
        doc.text(String(item.value || "—"), startX + labelWidth, y);
        y += 6;
    });
    return y;
}

// =========================
// Legacy Data Box
// =========================
export function drawDataBox(doc, y, title, items, { width = 85, startX = 15 } = {}) {
    const boxHeight = 12 + items.length * 5.5;
    doc.setFillColor(...COLORS.LIGHT_BG);
    doc.roundedRect(startX, y, width, boxHeight, 2, 2, "F");
    doc.setDrawColor(...COLORS.GRAY);
    doc.setLineWidth(0.3);
    doc.roundedRect(startX, y, width, boxHeight, 2, 2, "S");
    doc.setTextColor(...COLORS.DARK);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(title.toUpperCase(), startX + 4, y + 5);
    let iy = y + 11;
    doc.setTextColor(...COLORS.TEXT);
    doc.setFontSize(9);
    items.forEach((item) => {
        doc.setFont("helvetica", "normal");
        doc.text(item, startX + 4, iy);
        iy += 5.5;
    });
    return y + boxHeight + 4;
}

// =========================
// Signature Block
// =========================
export function drawSignatureBlock(doc, y) {
    const pageWidth = doc.internal.pageSize.getWidth();
    y = checkPageBreak(doc, y, 60);
    y += 5;
    doc.setDrawColor(...COLORS.GRAY);
    doc.setLineWidth(0.3);
    const leftX = 25;
    const rightX = pageWidth / 2 + 15;
    const sigWidth = 70;
    doc.setTextColor(...COLORS.TEXT);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("EL CLIENTE", leftX, y);
    doc.text("LOS TOLDOS ENTERTAINMENT", rightX, y);
    y += 25;
    doc.line(leftX, y, leftX + sigWidth, y);
    doc.line(rightX, y, rightX + sigWidth, y);
    y += 5;
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("Firma y fecha", leftX, y);
    doc.text("Firma y fecha", rightX, y);
    return y + 10;
}

// =========================
// Legacy Bullet List
// =========================
export function drawBulletList(doc, y, items, { startX = 20, fontSize = 9, lineHeight = 5.5 } = {}) {
    doc.setTextColor(...COLORS.TEXT);
    doc.setFontSize(fontSize);
    doc.setFont("helvetica", "normal");

    items.forEach((item) => {
        y = checkPageBreak(doc, y, lineHeight + 2);
        doc.text(`•  ${item}`, startX, y);
        y += lineHeight;
    });

    return y;
}

// =========================
// Minimalist Executive Header
// =========================
export function drawMinimalistHeader(doc, { docNumber = "", company = {} } = {}) {
    const pageWidth = doc.internal.pageSize.getWidth();

    // Logo en tinta negra, con su proporcion real (2,6:1). Antes se encajaba
    // en un cuadrado de 25x25 y salia aplastado; no se notaba porque el
    // fichero que se cargaba era el de fondo negro y solo se veia un recuadro.
    if (_logoBlackDataUrl) {
        try {
            doc.addImage(_logoBlackDataUrl, "PNG", 15, 15, 31.2, 12);
        } catch { /* ignore logo errors */ }
    }

    // Company legal info (Right side - dark gray)
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "normal");
    const rightX = pageWidth - 15;
    let textY = 15;

    // Use passed company data or fallback to defaults
    const cName = company.name || company.company_name || COMPANY.name;
    const cCif = company.cif || COMPANY.cif;
    const cAddress = company.address || company.fiscal_address || COMPANY.address;
    const cWeb = company.web || COMPANY.web;

    doc.text(cName, rightX, textY, { align: "right" });
    textY += 4.5;
    doc.text(`CIF: ${cCif}`, rightX, textY, { align: "right" });
    textY += 4.5;
    doc.text(cAddress, rightX, textY, { align: "right" });
    textY += 4.5;
    doc.text(cWeb, rightX, textY, { align: "right" });


    // Thin pink line separator
    doc.setDrawColor(...COLORS.PRIMARY);
    doc.setLineWidth(0.176); // 0.5pt
    doc.line(15, 42, pageWidth - 15, 42);

    return 55; // y position after header
}

// =========================
// Event Snapshot (3 columns)
// =========================
export function drawSnapshot(doc, y, { client = [], details = [], event = [] } = {}) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const colWidth = (pageWidth - 30) / 3;
    let startX = 15;

    const drawCol = (x, title, lines) => {
        doc.setFontSize(7.5);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...COLORS.PRIMARY);
        doc.text(title.toUpperCase(), x, y);

        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...COLORS.TEXT);
        let ly = y + 6;
        lines.forEach(line => {
            doc.text(String(line || "—"), x, ly);
            ly += 5;
        });
    };

    drawCol(startX, "Cliente", client);
    drawCol(startX + colWidth, "Detalles", details);
    drawCol(startX + colWidth * 2, "Evento", event);

    return y + 25;
}

// =========================
// Service Block (with dots/icons)
// =========================
export function drawServiceBlock(doc, y, title, items, { color = COLORS.BLACK } = {}) {
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...color);
    doc.text(title, 15, y);

    // Subtle line
    doc.setDrawColor(...COLORS.PRIMARY);
    doc.setLineWidth(0.176);
    doc.line(15, y + 2, 80, y + 2);

    y += 9;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.TEXT);

    items.forEach(item => {
        // Small pink dot as minimalist icon
        doc.setFillColor(...COLORS.PRIMARY);
        doc.circle(18, y - 1, 0.8, "F");
        doc.text(item, 23, y);
        y += 6;
    });

    return y + 4;
}

// =========================
// Exclusions Box
// =========================
export function drawExclusionsBox(doc, y, items) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const boxHeight = 12 + items.length * 5;

    // Very soft gray background
    doc.setFillColor(250, 250, 250);
    doc.roundedRect(15, y, pageWidth - 30, boxHeight, 2, 2, "F");

    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(150, 150, 150);
    doc.text("FUERA DE ALCANCE (EXCLUSIONES)", 20, y + 6);

    y += 11;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    items.forEach(item => {
        doc.text(`• ${item}`, 22, y);
        y += 5;
    });

    return y + 5;
}

// =========================
// Economic Summary "Golden Box"
// =========================
export function drawExecutiveSummary(doc, y, { subtotal, iva, total }) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const boxWidth = 70;
    const boxX = pageWidth - 15 - boxWidth;

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.TEXT);

    doc.text("Subtotal (Base Imponible):", boxX, y);
    doc.text(subtotal, pageWidth - 15, y, { align: "right" });

    y += 6;
    doc.text("IVA (21%):", boxX, y);
    doc.text(iva, pageWidth - 15, y, { align: "right" });

    y += 4;
    // Total highlight
    doc.setDrawColor(...COLORS.PRIMARY);
    doc.setLineWidth(0.5);
    doc.line(boxX, y, pageWidth - 15, y);

    y += 8;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.PRIMARY);
    doc.text("TOTAL PRESUPUESTO", boxX, y);
    doc.text(total, pageWidth - 15, y, { align: "right" });

    return y + 15;
}

// =========================
// Page Break Check
// =========================
export function checkPageBreak(doc, y, needed = 30) {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + needed > pageHeight - 25) {
        doc.addPage();
        return 25; // More margin top
    }
    return y;
}

// =========================
// Status Badge (e.g., proforma disclaimer)
// =========================
export function drawStatusBadge(doc, y, text, { type = "warning" } = {}) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const colors = {
        warning: { bg: [255, 248, 230], border: [245, 158, 11], text: [146, 64, 14] },
        info:    { bg: [239, 246, 255], border: [99, 102, 241], text: [30, 64, 175] },
        success: { bg: [236, 253, 245], border: [16, 185, 129], text: [6, 95, 70] },
    };
    const c = colors[type] || colors.warning;

    doc.setFillColor(...c.bg);
    doc.roundedRect(15, y, pageWidth - 30, 10, 1.5, 1.5, "F");
    doc.setDrawColor(...c.border);
    doc.setLineWidth(0.4);
    doc.roundedRect(15, y, pageWidth - 30, 10, 1.5, 1.5, "S");

    doc.setTextColor(...c.text);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(text.toUpperCase(), pageWidth / 2, y + 6.5, { align: "center" });

    return y + 14;
}

// =========================
// Paragraph Helper (wraps text)
// =========================
export function drawParagraph(doc, y, text, { startX = 15, maxWidth = 0, fontSize = 9, lineHeight = 4.5, align = "left", color = COLORS.TEXT } = {}) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const finalMaxWidth = maxWidth || (pageWidth - startX - 15);
    
    doc.setTextColor(...color);
    doc.setFontSize(fontSize);
    doc.setFont("helvetica", "normal");

    const lines = doc.splitTextToSize(text, finalMaxWidth);
    lines.forEach((line) => {
        y = checkPageBreak(doc, y, lineHeight + 2);
        doc.text(line, startX, y, { align });
        y += lineHeight;
    });

    return y;
}

// =========================
// Technical Rider Box
// =========================
export function drawRiderBox(doc, y, title, items) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const boxWidth = pageWidth - 30;
    
    doc.setFillColor(248, 250, 252); // Very light slate
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(0.2);
    
    const boxStartY = y;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.DARK);
    doc.text(title.toUpperCase(), 20, y + 7);
    
    y += 12;
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.TEXT);
    
    items.forEach(item => {
        y = checkPageBreak(doc, y, 6);
        doc.text(`> ${item}`, 22, y);
        y += 5;
    });

    const boxHeight = y - boxStartY + 2;
    doc.rect(15, boxStartY, boxWidth, boxHeight, "S");
    
    return y + 5;
}

// =========================
// Footer (call per page)
// =========================
export function drawFooter(doc, { docNumber = "" } = {}) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageCount = doc.internal.getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);

        // Very discreet thin line
        doc.setDrawColor(230, 230, 230);
        doc.setLineWidth(0.176);
        doc.line(15, pageHeight - 15, pageWidth - 15, pageHeight - 15);

        doc.setTextColor(180, 180, 180);
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        const parts = [COMPANY.shortName];
        if (docNumber) parts.push(docNumber);
        parts.push("Documento Confidencial", `Página ${i} de ${pageCount}`);

        doc.text(
            parts.join(" · "),
            pageWidth / 2,
            pageHeight - 10,
            { align: "center" }
        );
    }
}


// =========================
// Cabecera del contrato
// =========================
// El .docx de la plantilla no lleva banda de color ni bloque fiscal: solo el
// logotipo negro anclado en la cabecera, centrado y por encima del margen
// superior. Las medidas salen del propio anclaje del Word
// (wp:extent 1707958 x 655955 EMU = 47,4 x 18,2 mm, centrado sobre el margen).
export function drawContractLogo(doc) {
    if (!_logoBlackDataUrl) return;
    const pageWidth = doc.internal.pageSize.getWidth();
    const w = 47.4;
    const h = 18.2;
    try {
        doc.addImage(_logoBlackDataUrl, "PNG", (pageWidth - w) / 2, 5, w, h);
    } catch { /* el contrato se emite igual sin logo */ }
}
